import { flatten } from "./helpers";
import TagsAndAttributes from "./tags";

// https://developer.mozilla.org/en-US/docs/Web/SVG/Element

const Core = [
  'id', 'lang', 'tabindex', 'xml:base', 'xml:lang', 'xml:space'
]

const Styling = [
  'class', 'style'
]

const Conditional = [
  'requiredExtensions', 'requiredFeatures', 'systemLanguage'
]

const Presentation = [
  "alignment-baseline",
  "baseline-shift",
  "clip",
  "clip-path",
  "clip-rule",
  "color",
  "color-interpolation",
  "color-interpolation-filters",
  "color-profile",
  "color-rendering",
  "cursor",
  "d",
  "direction",
  "display",
  "dominant-baseline",
  "enable-background",
  "fill",
  "fill-opacity",
  "fill-rule",
  "filter",
  "flood-color",
  "flood-opacity",
  "font-family",
  "font-size",
  "font-size-adjust",
  "font-stretch",
  "font-style",
  "font-variant",
  "font-weight",
  "glyph-orientation-horizontal",
  "glyph-orientation-vertical",
  "image-rendering",
  "kerning",
  "letter-spacing",
  "lighting-color",
  "marker-end",
  "marker-mid",
  "marker-start",
  "mask",
  "opacity",
  "overflow",
  "pointer-events",
  "shape-rendering",
  "solid-color",
  "solid-opacity",
  "stop-color",
  "stop-opacity",
  "stroke",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-miterlimit",
  "stroke-opacity",
  "stroke-width",
  "text-anchor",
  "text-decoration",
  "text-rendering",
  "transform",
  "unicode-bidi",
  "vector-effect",
  "visibility",
  "word-spacing",
  "writing-mode"
]

const XLink = [
  "xlink:actuate",
  "xlink:arcrole",
  "xlink:href",
  "xlink:role",
  "xlink:show",
  "xlink:title",
  "xlink:type",
]

const ARIA = [
  "aria-activedescendant",
  "aria-atomic",
  "aria-autocomplete",
  "aria-busy",
  "aria-checked",
  "aria-colcount",
  "aria-colindex",
  "aria-colspan",
  "aria-controls",
  "aria-current",
  "aria-describedby",
  "aria-details",
  "aria-disabled",
  "aria-dropeffect",
  "aria-errormessage",
  "aria-expanded",
  "aria-flowto",
  "aria-grabbed",
  "aria-haspopup",
  "aria-hidden",
  "aria-invalid",
  "aria-keyshortcuts",
  "aria-label",
  "aria-labelledby",
  "aria-level",
  "aria-live",
  "aria-modal",
  "aria-multiline",
  "aria-multiselectable",
  "aria-orientation",
  "aria-owns",
  "aria-placeholder",
  "aria-posinset",
  "aria-pressed",
  "aria-readonly",
  "aria-relevant",
  "aria-required",
  "aria-roledescription",
  "aria-rowcount",
  "aria-rowindex",
  "aria-rowspan",
  "aria-selected",
  "aria-setsize",
  "aria-sort",
  "aria-valuemax",
  "aria-valuemin",
  "aria-valuenow",
  "aria-valuetext",
  "role"
]

const FilterPrimitive = [
  'height', 'result', 'width', 'x', 'y'
]

const TransferFunction = [
  'type', 'tableValues', 'slope', 'intercept', 'amplitude', 'exponent', 'offset'
]

const AnimationTarget = [
  "href"
]
const AnimationTiming = [
  "begin", "dur", "end", "min", "max", "restart", "repeatCount", "repeatDur", "fill"
]
const AnimationValue = [
  "calcMode", "values", "keyTimes", "keySplines", "from", "to", "by", "autoReverse", "accelerate", "decelerate"
]
const AnimationAddition = [
  "additive", "accumulate"
]

const Animation = flatten(AnimationTarget, AnimationTiming, AnimationValue, AnimationAddition)


const SvgScheme: TagsAndAttributes = {
  'a': flatten(Core, Styling, Conditional, Presentation, XLink, [
    'download', 'href', 'hreflang', 'ping', 'referrerpolicy', 'rel', 'target',
    'type', 'xlink:href'
  ]),
  'animate': flatten(Animation, Core, Styling),
  'animateMotion': flatten(Animation, Core, Styling, ['keyPoints', 'path', 'rotate']),
  'animateTransform': flatten(Conditional, Core, Animation, XLink, [
    'by', 'from', 'to'
  ]),
  'circle': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'cx', 'cy', 'r', 'pathLength'
  ]),
  'clipPath': flatten(Core, Styling, Conditional, Presentation, [
    'clipPathUnits'
  ]),
  'defs': flatten(Core, Styling, Presentation),
  'desc': flatten(Core, Styling),
  'discard': flatten(Conditional, Core, ARIA, [
    'begin', 'href'
  ]),
  'ellipse': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'cx', 'cy', 'rx', 'ry', 'pathLength'
  ]),
  'feBlend': flatten(Core, Presentation, Styling, FilterPrimitive, [
    'in', 'in2', 'mode'
  ]),
  'feColorMatrix': flatten(Core, Presentation, FilterPrimitive, Styling,[
    'in', 'type', 'values'
  ]),
  'feComponentTransfer': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in'
  ]),
  'feComposite': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'in2', 'operator', 'k1', 'k2', 'k3', 'k4'
  ]),
  'feConvolveMatrix': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'order', 'kernelMatrix', 'divisor', 'bias',
    'targetX', 'targetY', 'edgeMode', 'kernelUnitLength',
    'preserveAlpha'
  ]),
  'feDiffuseLighting': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'surfaceScale', 'diffuseConstant', 'kernelUnitLength'
  ]),
  'feDisplacementMap': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'in2', 'scale', 'xChannelSelector', 'yChannelSelector'
  ]),
  'feDistantLight': flatten(Core, [
    'azimuth', 'elevation'
  ]),
  'feDropShadow': flatten(Core, Styling, FilterPrimitive, Presentation, [
    'dx', 'dy', 'stdDeviation'
  ]),
  'feFlood': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'flood-color', 'flood-opacity'
  ]),
  'feFuncA': flatten(Core, TransferFunction),
  'feFuncB': flatten(Core, TransferFunction),
  'feFuncG': flatten(Core, TransferFunction),
  'feFuncR': flatten(Core, TransferFunction),
  'feGaussianBlur': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'stdDeviation', 'edgeMode'
  ]),
  'feImage': flatten(Core, Presentation, FilterPrimitive, XLink, Styling, [
    'preserveAspectRatio', 'xlink:href'
  ]),
  'feMerge': flatten(Core, Presentation, FilterPrimitive, Styling),
  'feMergeNode': flatten(Core, [
    'in'
  ]),
  'feMorphology': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'operator', 'radius'
  ]),
  'feOffset': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'dx', 'dy'
  ]),
  'fePointLight': flatten(Core, [
    'x', 'y', 'z'
  ]),
  'feSpecularLighting': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in', 'surfaceScale' ,'specularConstant' ,'specularExponent' ,'kernelUnitLength'
  ]),
  'feSpotLight': flatten(Core, [
    'x', 'y', 'z', 
    'pointsAtX', 'pointsAtY' ,'pointsAtZ',
    'specularExponent', 'limitingConeAngle'
  ]),
  'feTile': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'in'
  ]),
  'feTurbulence': flatten(Core, Presentation, FilterPrimitive, Styling, [
    'baseFrequency', 'numOctaves', 'seed' ,'stitchTiles' ,'type'
  ]),
  'filter': flatten(Core, Presentation, XLink, Styling, [
    'x', 'y', 'width', 'height', 'filterRes', 'filterUnits', 
    'primitiveUnits', 'xlink:href'
  ]),
  // NOTE: security hole
  // 'foreignObject': flatten(Core, Styling, Conditional, Presentation, ARIA, [
  //   'x', 'y', 'width', 'height',
  // ]),
  'g': flatten(Core, Styling, Conditional, Presentation, ARIA),
  'hatch': flatten(Core, Presentation, Styling, [
    'x', 'y', 'pitch', 'rotate', 'hatchUnits', 'hatchContentUnits', 
    'transform' , 'href'
  ]),
  'hatchpath': flatten(Core, Presentation, Styling, [
    'd', 'offset'
  ]),
  'image': flatten(Core, Conditional, Presentation, XLink, Styling, [
    'x', 'y', 'width', 'height','href', 'xlink:href',
    'preserveAspectRatio', 'crossorigin',
    'transform'
  ]),
  'linearGradient': flatten(Core, Styling, Presentation, XLink, [
    'gradientUnits', 'gradientTransform', 'href',
    'spreadMethod', 'x1', 'x2', 'xlink:href',
    'y1', 'y2'
  ]),
  'marker': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'markerHeight', 'markerUnits', 'markerWidth', 'orient',
    'preserveAspectRatio', 'refX', 'refY', 'viewBox'
  ]),
  'mask': flatten(Core, Styling, Conditional, Presentation, [
    'height', 'maskContentUnits', 'maskUnits',
    'x', 'y', 'width'
  ]),
  'mesh': [],
  'meshgradient': [],
  'meshpatch': [],
  'meshrow': [],
  'metadata': Core,
  'mpath': flatten(Core, XLink, [
    'xlink:href'
  ]),
  'path': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'd', 'pathLength'
  ]),
  'pattern': flatten(Core, Styling, Conditional, Presentation, XLink, [
    'height', 'href', 'patternContentUnits', 'patternTransform',
    'patternUnits', 'preserveAspectRatio', 'viewBox', 'width', 
    'x', 'xlink:href', 'y'
  ]),
  'polygon': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'points', 'pathLength'
  ]),
  'polyline': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'points', 'pathLength'
  ]),
  'radialGradient': flatten(Core, Styling, Presentation, XLink, [
    'cx', 'cy', 'fr', 'fx', 'fy', 'gradientUnits',
    'gradientTransform', 'href', 'r', 'spreadMethod',
    'xlink:href'
  ]),
  'rect': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'x', 'y', 'width', 'height', 'rx', 'ry', 'pathLength'
  ]),
  // NOTE: security hole
  // 'script': flatten(Core, [
  //   ''
  // ]),
  'set': flatten(Animation, Core, Styling, [
    'to'
  ]),
  'stop': flatten(Core, Styling, Presentation, [
    'offset', 'stop-color', 'stop-opacity'
  ]),
  // NOTE: unsafe
  // 'style': flatten(Core, Styling, [
  //   'type', 'media', 'title'
  // ]),
  'svg': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'baseProfile', 'contentScriptType', 'contentStyleType',
    'height', 'preserveAspectRatio',
    'version', 'viewBox', 'width', 'x', 'y'
  ]),
  'switch': flatten(Core, Conditional, Presentation, Styling, [
    'transform'
  ]),
  'symbol': flatten(Core, Styling, Presentation, ARIA, [
    'height', 'preserveAspectRatio', 'refX', 'refY', 'width', 'x', 'y'
  ]),
  'text': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'x', 'y', 'dx', 'dy', 'rotate', 'lengthAdjust', 'textLength',
    'font-family', 'font-size', 'font-size-adjust', 'font-stretch',
    'font-style', 'font-variant', 'font-weight'
  ]),
  'textPath': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'href', 'lengthAdjust', 'method', 'path', 'side',
    'spacing', 'startOffset', 'textLength'
  ]),
  'title': flatten(Core, Styling),
  'tspan': flatten(Core, Styling, Conditional, Presentation, ARIA, [
    'x', 'y', 'dx', 'dy', 'rotate', 'lengthAdjust', 'textLength',
  ]),
  'use': flatten(Core, Styling, Presentation, XLink, Presentation, [
    'href', 'xlink:href', 'x', 'y', 'width', 'height'
  ]),
  'view': flatten(Core, ARIA, [
    'viewBox', 'preserveAspectRatio', 'zoomAndPan', 'viewTarget'
  ]),
}

export default SvgScheme;